import React, { useState, useEffect } from 'react';
import './PropertyDetails.css';
import Card from '../common/Card';
import PropertyModal from './PropertyModal';
import UtilityModal from './UtilityModal';
import { auth } from '../../services/api';

interface Address {
  address_type: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
}

interface UtilityAccount {
  id: number;
  utility_type: string;
  provider_name: string;
  account_number: string;
  procurement_number?: string;
  annual_budget?: number;
  active: boolean;
}

interface Property {
  id: number;
  client_id: number;
  property_name: string;
  service_point_name: string;
  jump_service_point_id: string;
  jump_service_point_seq: number;
  jump_property_seq_no: number;
  jump_property_id: string;
  jump_property_group: string;
  len_legal_entity_name: string;
  property_address: string;
  property_address2?: string;
  city: string;
  state: string;
  zip_code: string;
  energy_star_property_type: string;
  occupancy_type: string;
  occupancy_percent: number;
  year_built: number;
  tax_exempt_percent: string;
  total_gross_floor_area: number;
  number_of_buildings: number;
  has_irrigated_area: boolean;
  longitude: string;
  latitude: string;
  active: boolean;
  active_date: string;
  created_at: string;
  updated_at: string;
  addresses: Address[];
  utility_count?: number;
  active_accounts?: number;
  utilities_list?: UtilityAccount[];
}

interface PropertyDetailsProps {
  clientId: number;
  propertyId: number;
  onBack: () => void;
}

const PropertyDetails: React.FC<PropertyDetailsProps> = ({
  clientId,
  propertyId,
  onBack
}) => {
  const getUtilityIcon = (type: string) => {
    const icons = {
      'Electric': '⚡',
      'Gas': '🔥',
      'Water': '💧',
      'CWS': '❄️'
    };
    return icons[type as keyof typeof icons] || '📄';
  };

  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  const [propertyModal, setPropertyModal] = useState({ isOpen: false, mode: 'edit' as 'add' | 'edit' });
  const [utilityModal, setUtilityModal] = useState({ isOpen: false, mode: 'add' as 'add' | 'edit', utilityAccountId: undefined as number | undefined });

  useEffect(() => {
    fetchProperty();
  }, [propertyId]);

  const fetchProperty = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/properties/${propertyId}`, {
        headers: { ...auth.getAuthHeaders() }
      });
      if (response.ok) {
        const data = await response.json();
        setProperty(data);
      }
    } catch (error) {
      console.error('Error fetching property:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditProperty = () => {
    setPropertyModal({ isOpen: true, mode: 'edit' });
  };

  const handleClosePropertyModal = () => {
    setPropertyModal({ isOpen: false, mode: 'edit' });
  };

  const handlePropertySuccess = () => {
    fetchProperty();
  };

  const handleAddUtility = () => {
    setUtilityModal({ isOpen: true, mode: 'add', utilityAccountId: undefined });
  };

  const handleEditUtility = (utilityAccountId: number) => {
    setUtilityModal({ isOpen: true, mode: 'edit', utilityAccountId });
  };

  const handleCloseUtilityModal = () => {
    setUtilityModal({ isOpen: false, mode: 'add', utilityAccountId: undefined });
  };

  const handleUtilitySuccess = () => {
    fetchProperty();
  };

  const getPropertyInitials = (property: Property) => {
    return (property.service_point_name || property.property_name).substring(0, 2).toUpperCase();
  };

  if (!property) {
    if (loading) {
      return <div className="loading">Loading property details...</div>;
    }
    return <div className="error">Property not found</div>;
  }

  return (
    <div className="property-details">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <button type="button" onClick={onBack} className="breadcrumb-pill">
          Client Details
        </button>
        <span className="breadcrumb-separator">/</span>
        <button type="button" className="breadcrumb-pill active">
          Property Details
        </button>
      </div>

      {/* Property Information Card */}
      <Card style={{marginBottom: '1rem'}}>
        {/* Property Header with Edit */}
        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', paddingBottom: '0.75rem'}}>
          <h1 style={{fontSize: '1.25rem', fontWeight: 700, color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
            <div style={{width: '2rem', height: '2rem', background: '#0066cc', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.75rem', fontWeight: 600}}>
              {getPropertyInitials(property)}
            </div>
            {property.service_point_name || property.property_name}
          </h1>
          <button type="button" className="btn-edit" onClick={handleEditProperty}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            Edit Property
          </button>
        </div>

        {/* Property Details Grid - 5 Columns */}
        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '1.5rem'}}>
          {/* SERVICE POINT Section */}
          <div style={{display: 'flex', flexDirection: 'column', gap: '0.6875rem'}}>
            <div style={{fontSize: '0.75rem', fontWeight: 600, color: '#0066cc', textTransform: 'uppercase', letterSpacing: '0.05em', paddingBottom: '0.25rem', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
              <svg style={{width: '0.75rem', height: '0.75rem'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              SERVICE POINT DETAILS
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.6875rem'}}>
              <svg style={{width: '0.875rem', height: '0.875rem', flexShrink: 0, color: '#0ea5e9'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span style={{fontSize: '0.75rem', fontWeight: 500, color: '#1f2937'}} data-tooltip="Service point name">{property.service_point_name || '-'}</span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.6875rem'}}>
              <svg style={{width: '0.875rem', height: '0.875rem', flexShrink: 0, color: '#0ea5e9'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
              <span style={{fontSize: '0.75rem', fontWeight: 500, color: '#1f2937'}} data-tooltip="Service point ID">{property.jump_service_point_id || '-'}</span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.6875rem'}}>
              <svg style={{width: '0.875rem', height: '0.875rem', flexShrink: 0, color: '#0ea5e9'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
              </svg>
              <span style={{fontSize: '0.75rem', fontWeight: 500, color: '#1f2937'}} data-tooltip="Service point sequence">{property.jump_service_point_seq || '-'}</span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.6875rem'}}>
              <svg style={{width: '0.875rem', height: '0.875rem', flexShrink: 0, color: '#6366f1'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span style={{fontSize: '0.75rem', fontWeight: 500, color: '#1f2937'}} data-tooltip="Legal entity name">{property.len_legal_entity_name || '-'}</span>
            </div>
          </div>

          {/* PROPERTY DETAILS Section */}
          <div style={{display: 'flex', flexDirection: 'column', gap: '0.6875rem'}}>
            <div style={{fontSize: '0.75rem', fontWeight: 600, color: '#0066cc', textTransform: 'uppercase', letterSpacing: '0.05em', paddingBottom: '0.25rem', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
              <svg style={{width: '0.75rem', height: '0.75rem'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h3M9 7h6m-6 4h6m-6 4h6" />
              </svg>
              PROPERTY DETAILS
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.6875rem'}}>
              <svg style={{width: '0.875rem', height: '0.875rem', flexShrink: 0, color: '#8b5cf6'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h3M9 7h6m-6 4h6m-6 4h6" />
              </svg>
              <span style={{fontSize: '0.75rem', fontWeight: 500, color: '#1f2937'}} data-tooltip="Property type">{property.energy_star_property_type || '-'}</span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.6875rem'}}>
              <svg style={{width: '0.875rem', height: '0.875rem', flexShrink: 0, color: '#f59e0b'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span style={{fontSize: '0.75rem', fontWeight: 500, color: '#1f2937'}} data-tooltip="Year built">{property.year_built || '-'}</span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.6875rem'}}>
              <svg style={{width: '0.875rem', height: '0.875rem', flexShrink: 0, color: '#ef4444'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <rect x="4" y="4" width="6" height="16" strokeWidth="2"/>
                <rect x="14" y="8" width="6" height="12" strokeWidth="2"/>
              </svg>
              <span style={{fontSize: '0.75rem', fontWeight: 500, color: '#1f2937'}} data-tooltip="Number of buildings">{property.number_of_buildings || '-'}</span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.6875rem'}}>
              <svg style={{width: '0.875rem', height: '0.875rem', flexShrink: 0, color: '#06b6d4'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <rect x="3" y="6" width="18" height="12" rx="2" strokeWidth="2"/>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 12h12M12 6v12" />
              </svg>
              <span style={{fontSize: '0.75rem', fontWeight: 500, color: '#1f2937'}} data-tooltip="Total gross floor area">{property.total_gross_floor_area ? `${property.total_gross_floor_area.toLocaleString()} sq ft` : '-'}</span>
            </div>
          </div>

          {/* OCCUPANCY & TAX Section */}
          <div style={{display: 'flex', flexDirection: 'column', gap: '0.6875rem'}}>
            <div style={{fontSize: '0.75rem', fontWeight: 600, color: '#0066cc', textTransform: 'uppercase', letterSpacing: '0.05em', paddingBottom: '0.25rem', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
              <svg style={{width: '0.75rem', height: '0.75rem'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              OCCUPANCY & TAX
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.6875rem'}}>
              <svg style={{width: '0.875rem', height: '0.875rem', flexShrink: 0, color: '#ec4899'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span style={{fontSize: '0.75rem', fontWeight: 500, color: '#1f2937'}} data-tooltip="Occupancy type">{property.occupancy_type || '-'}</span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.6875rem'}}>
              <svg style={{width: '0.875rem', height: '0.875rem', flexShrink: 0, color: '#ec4899'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" strokeWidth={2}/>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6l4 2" />
              </svg>
              <span style={{fontSize: '0.75rem', fontWeight: 500, color: '#1f2937'}} data-tooltip="Occupancy percentage">{property.occupancy_percent ? `${Math.round(property.occupancy_percent)}%` : '-'}</span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.6875rem'}}>
              <svg style={{width: '0.875rem', height: '0.875rem', flexShrink: 0, color: '#ec4899'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <span style={{fontSize: '0.75rem', fontWeight: 500, color: '#1f2937'}} data-tooltip="Tax exempt percentage">{property.tax_exempt_percent !== null && property.tax_exempt_percent !== undefined ? `${Math.round(Number(property.tax_exempt_percent))}% Tax Exempt` : '-'}</span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.6875rem'}}>
              <svg style={{width: '0.875rem', height: '0.875rem', flexShrink: 0, color: '#22c55e'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
              <span style={{fontSize: '0.75rem', fontWeight: 500, color: '#1f2937'}} data-tooltip="Has irrigated area">{property.has_irrigated_area ? 'Yes' : 'No'}</span>
            </div>
          </div>

          {/* ADDRESS Section */}
          <div style={{display: 'flex', flexDirection: 'column', gap: '0.6875rem'}}>
            <div style={{fontSize: '0.75rem', fontWeight: 600, color: '#0066cc', textTransform: 'uppercase', letterSpacing: '0.05em', paddingBottom: '0.25rem', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
              <svg style={{width: '0.75rem', height: '0.75rem'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              ADDRESS
            </div>
            {property.addresses && property.addresses.length > 0 ? (
              property.addresses.map((address, index) => (
                <div key={index} style={{display: 'flex', alignItems: 'flex-start', gap: '0.6875rem'}}>
                  <svg
                    style={{width: '0.875rem', height: '0.875rem', flexShrink: 0, marginTop: '0.125rem', color: '#22c55e', cursor: property.latitude && property.longitude ? 'pointer' : 'default'}}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    onClick={() => {
                      if (property.latitude && property.longitude) {
                        window.open(`https://maps.google.com/?q=${property.latitude},${property.longitude}`, '_blank');
                      }
                    }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div style={{fontSize: '0.75rem', fontWeight: 500, color: '#1f2937', lineHeight: 1.4}} data-tooltip={`${address.address_type.charAt(0).toUpperCase() + address.address_type.slice(1)} address`}>
                    {address.address_line1 && <div>{address.address_line1}</div>}
                    {address.address_line2 && <div>{address.address_line2}</div>}
                    {(address.city || address.state || address.zip_code) && (
                      <div>
                        {address.city}{address.city && address.state && ', '}{address.state}{(address.city || address.state) && address.zip_code && ' '}{address.zip_code}
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div style={{display: 'flex', alignItems: 'flex-start', gap: '0.6875rem'}}>
                <svg
                  style={{width: '0.875rem', height: '0.875rem', flexShrink: 0, marginTop: '0.125rem', color: '#22c55e', cursor: property.latitude && property.longitude ? 'pointer' : 'default'}}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  onClick={() => {
                    if (property.latitude && property.longitude) {
                      window.open(`https://maps.google.com/?q=${property.latitude},${property.longitude}`, '_blank');
                    }
                  }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <div style={{fontSize: '0.75rem', fontWeight: 500, color: '#1f2937', lineHeight: 1.4}} data-tooltip="Property address">
                  <div>{property.property_address}</div>
                  {property.property_address2 && <div>{property.property_address2}</div>}
                  <div>{property.city}, {property.state} {property.zip_code}</div>
                </div>
              </div>
            )}
          </div>

          {/* STATUS Section */}
          <div style={{display: 'flex', flexDirection: 'column', gap: '0.6875rem'}}>
            <div style={{fontSize: '0.75rem', fontWeight: 600, color: '#0066cc', textTransform: 'uppercase', letterSpacing: '0.05em', paddingBottom: '0.25rem', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
              <svg style={{width: '0.75rem', height: '0.75rem'}} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M9 12l2 2 4-4"/>
              </svg>
              STATUS
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.6875rem'}}>
              <svg className={property.active ? 'icon-status-active' : 'icon-status-inactive'} style={{width: '0.875rem', height: '0.875rem', flexShrink: 0}} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                {property.active ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 9l-6 6M9 9l6 6" />
                )}
              </svg>
              <span style={{fontSize: '0.75rem', fontWeight: 500, color: '#1f2937'}} data-tooltip="Property status">
                {property.active ? (
                  property.active_date ? `Active as of ${new Date(property.active_date).toLocaleDateString('en-US')}` : 'Active'
                ) : (
                  property.active_date ? `Inactive as of ${new Date(property.active_date).toLocaleDateString('en-US')}` : 'Inactive'
                )}
              </span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.6875rem'}}>
              <svg style={{width: '0.875rem', height: '0.875rem', flexShrink: 0, color: '#22c55e'}} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
              </svg>
              <span style={{fontSize: '0.75rem', fontWeight: 500, color: '#1f2937'}} data-tooltip="Created on">
                {property.created_at ? new Date(property.created_at).toLocaleDateString('en-US') : 'Not available'}
              </span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.6875rem'}}>
              <svg style={{width: '0.875rem', height: '0.875rem', flexShrink: 0, color: '#f59e0b'}} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              <span style={{fontSize: '0.75rem', fontWeight: 500, color: '#1f2937'}} data-tooltip="Last updated">
                {property.updated_at ? new Date(property.updated_at).toLocaleDateString('en-US') : 'Not available'}
              </span>
            </div>
          </div>
        </div>
      </Card>

      {/* Utility Accounts Section */}
      <Card style={{marginBottom: '1rem'}}>
        <div className="section-header">
          <h2>Utility Accounts ({property.utility_count || 0})</h2>
          <button type="button" className="btn-primary" onClick={handleAddUtility}>
            + Add Utility
          </button>
        </div>

        {property.utilities_list && property.utilities_list.length > 0 ? (
          <div className="properties-grid-six-col">
            {property.utilities_list.filter(account => account.active).map((account: any) => (
              <div
                key={account.id}
                onClick={() => handleEditUtility(account.id)}
                style={{
                  padding: '0.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.375rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#0066cc';
                  e.currentTarget.style.boxShadow = '0 0.25rem 0.5rem rgba(0, 102, 204, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <span style={{fontSize: '1.25rem'}}>
                  {getUtilityIcon(account.utility_type)}
                </span>
                <div>
                  <div style={{fontWeight: 500, fontSize: '0.75rem', color: '#1f2937'}}>
                    {account.provider_name}
                  </div>
                  <div style={{fontSize: '0.6875rem', color: '#6b7280', marginTop: '0.25rem'}}>
                    #{account.account_number}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-utility-accounts">
            <p>No utility accounts found</p>
          </div>
        )}
      </Card>

      <PropertyModal
        isOpen={propertyModal.isOpen}
        onClose={handleClosePropertyModal}
        clientId={clientId}
        propertyId={propertyId}
        mode={propertyModal.mode}
        onSuccess={handlePropertySuccess}
      />

      <UtilityModal
        isOpen={utilityModal.isOpen}
        onClose={handleCloseUtilityModal}
        propertyId={propertyId}
        utilityAccountId={utilityModal.utilityAccountId}
        mode={utilityModal.mode}
        onSuccess={handleUtilitySuccess}
        property={property}
      />
    </div>
  );
};

export default PropertyDetails;
